import type { ApolloQueryResult } from "@apollo/client";
import dayjs, { extend } from "dayjs";
import utc from "dayjs/plugin/utc";
import type React from "react";
import { createContext, useEffect } from "react";

import type { Exact, GetCurrentUserAtHomeQuery } from "gql/graphql";
import { getCookie } from "utils/apollo";
import { Logger } from "utils/logger";
import { logOut } from "utils/logout";
import { translate } from "utils/translations/translate";

interface IUser {
  refetch?: (
    variables?: Partial<Exact<Record<string, never>>>,
  ) => Promise<ApolloQueryResult<GetCurrentUserAtHomeQuery>>;
  tours: {
    newGroup: boolean;
    newRoot: boolean;
    welcome: boolean;
  };
  trialAdmin?: boolean;
  userEmail: string;
  userInTrial?: boolean;
  userName: string;
}

interface IAuthContext extends IUser {
  setUser?: React.Dispatch<React.SetStateAction<IUser>>;
}

const authContext = createContext<IAuthContext>({
  tours: {
    newGroup: false,
    newRoot: false,
    welcome: false,
  },
  userEmail: "",
  userName: "",
});

extend(utc);

const useSessionCheck = (expDate: string | undefined): void => {
  useEffect((): (() => void) | undefined => {
    if (expDate === undefined) {
      return undefined;
    }
    const timer = setTimeout((): void => {
      logOut("replace");
      // eslint-disable-next-line no-alert -- Deliberate usage
      alert(translate.t("validations.validSessionDate"));
    }, dayjs.utc(expDate).diff(dayjs.utc()));

    return (): void => {
      clearTimeout(timer);
    };
  }, [expDate]);
};

const verifyCredentials = async (): Promise<boolean> => {
  /**
   * Uses fetch instead of Apollo Client to verify credentials without triggering
   * Apollo's automatic retry logic, which would incorrectly retry auth failures.
   */
  const ok = 200;

  try {
    const response = await fetch(`${window.location.origin}/api`, {
      body: JSON.stringify({
        operationName: "VerifyCredentials",
        query: "query VerifyCredentials { me { userEmail } }",
      }),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCookie("csrftoken"),
      },
      method: "POST",
    });

    if (response.status !== ok) return false;

    const data: {
      data: { me: object; errors?: object[] };
    } = await response.json();

    return !("errors" in data.data);
  } catch (error) {
    Logger.error("Couldn't verify credentials", {
      error: error instanceof Error ? error.message : String(error),
    });

    return false;
  }
};

export type { IAuthContext };
export { authContext, useSessionCheck, verifyCredentials };
